import { Workbox } from 'workbox-window';

import { getJwtSubject } from './utils/jwt';

const SUPPORTED_TARGETS = new Set(['members', 'admin']);

const getActiveToken = (target) => {
  if (target === 'admin') return localStorage.getItem('googleAuthToken');
  return localStorage.getItem('memberToken') || localStorage.getItem('customerToken');
};

const getUserSub = (target) => getJwtSubject(getActiveToken(target)) || 'anonymous';

const postUserContext = (registration, target) => {
  const controller = registration?.active || registration?.waiting || navigator.serviceWorker?.controller;
  // `controller` must be a live ServiceWorker. In some workbox-window code paths
  // a non-worker (e.g. the Workbox instance, whose `active` is a Promise) can
  // reach here; calling postMessage on it throws "postMessage is not a function".
  if (!controller || typeof controller.postMessage !== 'function') return;
  controller.postMessage({
    type: 'SET_USER_CONTEXT',
    target,
    userSub: getUserSub(target),
  });
};

const wireStorageSync = (registration, target) => {
  window.addEventListener('storage', (event) => {
    const memberKeys = ['memberToken', 'customerToken'];
    const adminKeys = ['googleAuthToken'];
    const relevant = target === 'admin' ? adminKeys : memberKeys;
    if (relevant.includes(event.key)) postUserContext(registration, target);
  });
};

export const clearServiceWorkerUserCaches = async (target) => {
  if (!SUPPORTED_TARGETS.has(target) || !('serviceWorker' in navigator)) return;
  const registration = await navigator.serviceWorker.ready.catch(() => null);
  const controller = registration?.active || registration?.waiting || navigator.serviceWorker.controller;
  controller?.postMessage({ type: 'CLEAR_USER_CACHE', target });
};

export const registerTargetServiceWorker = (target = process.env.REACT_APP_TARGET) => {
  if (!SUPPORTED_TARGETS.has(target)) return null;
  if (process.env.NODE_ENV !== 'production') return null;
  if (!('serviceWorker' in navigator)) return null;

  const wb = new Workbox('/sw.js');

  wb.addEventListener('activated', () => {
    // Use the registered ServiceWorkerRegistration, not the workbox event
    // target (the Workbox instance), whose `active` getter is a Promise.
    postUserContext(wb?.registration, target);
  });

  wb.register()
    .then((registration) => {
      postUserContext(registration, target);
      wireStorageSync(registration, target);
      navigator.serviceWorker.addEventListener('controllerchange', () => postUserContext(registration, target));
    })
    .catch((error) => {
      // PWA support is progressive enhancement; never break portal boot.
       
      console.warn('Service worker registration failed:', error);
    });

  return wb;
};
